import { type FormEventHandler, type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router-dom';

import ActionForm from '~/components/ActionForm';
import StatusInputs from '~/components/Forms/StatusInputs';
import type { ToastData } from '~/hooks/useToast';
import toast from '~/utils/toast';

type Props = {
  currentSubscriberEmail: string;
  currentSubscriberFirstName: string;
  currentSubscriberLastName: string;
  subscriberId: string;
  children: ({ isLoading, onAvatarSelect }: { isLoading: boolean; onAvatarSelect: (file: File) => void }) => ReactNode;
  onSuccess?: () => void;
};

const UpdateProfileAction = ({
  children,
  onSuccess,
  subscriberId,
  currentSubscriberEmail,
  currentSubscriberFirstName,
  currentSubscriberLastName,
}: Props) => {
  const { t } = useTranslation();
  const fetcher = useFetcher<{ toast?: ToastData }>();
  const isLoading = fetcher.state === 'submitting';
  const avatarFileRef = useRef<File | null>(null);

  const handleAvatarSelect = (file: File) => {
    avatarFileRef.current = file;
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const hasEmailChanged = formData.get('email') !== currentSubscriberEmail;
    const hasNameChanged =
      formData.get('first_name') !== currentSubscriberFirstName ||
      formData.get('last_name') !== currentSubscriberLastName;
    const hasAvatarChanged = avatarFileRef.current !== null;

    if (!hasEmailChanged && !hasNameChanged && !hasAvatarChanged) {
      onSuccess?.();
      return;
    }

    // Add avatar to formData if changed
    if (avatarFileRef.current) {
      formData.append('avatar', avatarFileRef.current);
    }

    if (hasEmailChanged && (hasNameChanged || hasAvatarChanged)) {
      return fetcher.submit(formData, { method: 'post', action: '/profile_and_email', encType: 'multipart/form-data' });
    }

    if (hasEmailChanged) {
      return fetcher.submit(formData, { method: 'post', action: '/profile_email' });
    }

    return fetcher.submit(formData, { method: 'post', action: '/profile', encType: 'multipart/form-data' });
  };

  return (
    <ActionForm
      fetcher={fetcher}
      method="post"
      action="/profile_and_email"
      onSubmit={handleSubmit}
      onSuccess={(data: { toast?: ToastData }) => {
        onSuccess?.();
        if (data?.toast?.message) {
          toast.success(data.toast.message);
        }
      }}
      onError={(data: { toast?: ToastData }) => {
        toast.error(data?.toast?.message || 'Something went wrong, please try again');
      }}
    >
      <StatusInputs name="profile" successMessage={t('toast.success')} errorMessage={t('errors.default')} />
      <input type="hidden" name="email_success_message" value={t('manage.update_profile_modal.toast.success')} />
      <input type="hidden" name="email_default_error_message" value={t('manage.update_profile_modal.toast.error')} />
      <input
        type="hidden"
        name="email_invalid_error_message"
        value={t('manage.update_profile_modal.toast.error_invalid_email')}
      />
      <input
        type="hidden"
        name="email_unique_error_message"
        value={t('manage.update_profile_modal.toast.error_email_exists')}
      />
      {subscriberId && <input type="hidden" name="subscription_id" defaultValue={subscriberId} />}
      {children({ isLoading, onAvatarSelect: handleAvatarSelect })}
    </ActionForm>
  );
};

export default UpdateProfileAction;
